<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue'
import { bbox, bboxPolygon, geometryCollection, intersect, area } from '@turf/turf'
import { MapSource, MapLayer } from '@/components/map'
import { Eye } from 'lucide-vue-next'
import { Spinner } from '@/components/ui/spinner'

const props = defineProps({
  sceneFeature: {
    type: Object,
    required: true
  },
  userGeometry: {
    type: Object,
    required: true
  },
  map: {
    type: Object,
    required: true
  },
  imageUrl: {
    type: String,
    required: true
  }
})

const corners = computed(() => {
  const [minLng, minLat, maxLng, maxLat] = bbox(props.sceneFeature)
  return [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat]
  ]
})

function latLonToCanvasXY(lng, lat, minLng, maxLng, minLat, maxLat, canvasWidth, canvasHeight) {
  const w = maxLng - minLng
  const h = maxLat - minLat
  const x = ((lng - minLng) / w) * canvasWidth
  const y = ((maxLat - lat) / h) * canvasHeight
  return [x, y]
}

const clippedImageUrl = ref(null)
const coveragePercentage = ref(0)
const clippedCanvas = ref(null)
const canvasOutput = ref(null)
const isPreview = ref(false)

function renderCanvasPreviewInUrl() {
  const [minLng, minLat, maxLng, maxLat] = bbox(props.sceneFeature)
  const w = maxLng - minLng
  const h = maxLat - minLat

  const boundingBoxPoly = bboxPolygon([minLng, minLat, maxLng, maxLat])
  const intersectionPoly = intersect(
    geometryCollection([boundingBoxPoly.geometry, props.userGeometry])
  )
  if (intersectionPoly) {
    const intersectionArea = area(intersectionPoly)
    const boundingBoxArea = area(boundingBoxPoly)
    coveragePercentage.value = (intersectionArea / boundingBoxArea) * 100
  } else {
    coveragePercentage.value = 0
  }

  const canvasSize = 1024
  const aspectRatio = w / h
  const canvasWidth = aspectRatio >= 1 ? canvasSize : Math.round(canvasSize * aspectRatio)
  const canvasHeight = aspectRatio >= 1 ? Math.round(canvasSize / aspectRatio) : canvasSize
  const canvas = document.createElement('canvas')
  canvas.width = canvasWidth
  canvas.height = canvasHeight
  const ctx = canvas.getContext('2d')
  const outerRing = props.userGeometry.coordinates[0]

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  ctx.beginPath()
  if (outerRing && outerRing.length > 0) {
    const [lng0, lat0] = outerRing[0]
    const [x0, y0] = latLonToCanvasXY(lng0, lat0, minLng, maxLng, minLat, maxLat, canvasWidth, canvasHeight)
    ctx.moveTo(x0, y0)
    for (let i = 1; i < outerRing.length; i++) {
      const [lngI, latI] = outerRing[i]
      const [xI, yI] = latLonToCanvasXY(lngI, latI, minLng, maxLng, minLat, maxLat, canvasWidth, canvasHeight)
      ctx.lineTo(xI, yI)
    }
    ctx.closePath()
  }
  ctx.clip()

  const url = new URL(props.imageUrl)
  url.searchParams.set('size', '1024')
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = url.href

  return new Promise((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
      clippedImageUrl.value = canvas.toDataURL('image/png')

      const userOuterRing = props.userGeometry.coordinates[0] || []
      const xCoords = []
      const yCoords = []
      for (const [lng, lat] of userOuterRing) {
        const [x, y] = latLonToCanvasXY(lng, lat, minLng, maxLng, minLat, maxLat, canvasWidth, canvasHeight)
        xCoords.push(x)
        yCoords.push(y)
      }
      const minX = Math.max(0, Math.floor(Math.min(...xCoords)))
      const maxX = Math.min(canvasWidth, Math.ceil(Math.max(...xCoords)))
      const minY = Math.max(0, Math.floor(Math.min(...yCoords)))
      const maxY = Math.min(canvasHeight, Math.ceil(Math.max(...yCoords)))
      const clippedW = Math.max(1, maxX - minX)
      const clippedH = Math.max(1, maxY - minY)
      
      try {
        const croppedCanvas = document.createElement('canvas')
        croppedCanvas.width = clippedW
        croppedCanvas.height = clippedH
        const croppedCtx = croppedCanvas.getContext('2d')
        
        if (clippedW > 0 && clippedH > 0 && clippedW <= canvasWidth && clippedH <= canvasHeight) {
          const clippedData = ctx.getImageData(minX, minY, clippedW, clippedH)
          croppedCtx.putImageData(clippedData, 0, 0)
          clippedCanvas.value = croppedCanvas
        } else {
          console.error('Invalid canvas dimensions:', { clippedW, clippedH })
          croppedCtx.fillStyle = '#f0f0f0'
          croppedCtx.fillRect(0, 0, clippedW, clippedH)
          clippedCanvas.value = croppedCanvas
        }
      } catch (error) {
        console.error('Canvas rendering error:', error)
        const fallbackCanvas = document.createElement('canvas')
        fallbackCanvas.width = 256
        fallbackCanvas.height = 256
        const fallbackCtx = fallbackCanvas.getContext('2d')
        fallbackCtx.fillStyle = '#f0f0f0'
        fallbackCtx.fillRect(0, 0, 256, 256)
        clippedCanvas.value = fallbackCanvas
      }
      resolve()
    }
    img.onerror = reject
  })
}

function renderMapPreview() {
  if (!isPreview.value) return
}

onMounted(async () => {
  await renderCanvasPreviewInUrl()
  await nextTick()
  if (canvasOutput.value && clippedCanvas.value) {
    canvasOutput.value.width = clippedCanvas.value.width
    canvasOutput.value.height = clippedCanvas.value.height
    const finalCtx = canvasOutput.value.getContext('2d')
    finalCtx.drawImage(clippedCanvas.value, 0, 0)
  }
})

watch(isPreview, (val) => {
  if (val) renderMapPreview()
})
</script>

<template>
  <MapSource
    v-if="clippedImageUrl && isPreview"
    :map="map"
    type="image"
    :id="`clipped-image-source-${sceneFeature.id}`"
    :url="clippedImageUrl"
    :coordinates="corners"
  >
    <MapLayer
      :map="map"
      :id="`clipped-image-layer-${sceneFeature.id}`"
      type="raster"
      :source="`clipped-image-source-${sceneFeature.id}`"
      :options="{
        paint: {
          'raster-opacity': 1,
          'raster-fade-duration': 0,
          'raster-resampling': 'nearest'
        }
      }"
    />
  </MapSource>
  <button
    type="button"
    class="min-w-32 min-h-32 w-32 h-32 border relative rounded cursor-pointer"
    :class="{'border-primary': isPreview}"
    @click="isPreview = !isPreview"
  >
    <div class="absolute top-0.5 left-0.5 flex items-center space-x-1 bg-gray-300 rounded p-1">
      <Eye class="w-4.5 h-4.5" />
    </div>
    <canvas
      v-if="clippedCanvas"
      ref="canvasOutput"
      class="w-full h-full object-cover rounded bg-gray-100 p-1.5"
    />
    <div v-else class="w-full h-full object-cover rounded bg-gray-100 flex items-center justify-center">
      <Spinner class="w-6 h-6" />
    </div>
  </button>
</template>
